import assertNever from "assert-never";
import React from "react";
import { isLoading } from "../../Loading";
import {
  CaregiverSurveyQuestionAnswerId,
  CaregiverSurveyQuestionId,
  CaregiverSurveyQuestionWithPossibleAnswers,
  OrientationLanguages,
} from "../../messages";
import { AppAbstractModal } from "../Generic/AppModal/AppAbstractModal";
import { SurveyState } from "./CaregiverSurveyActivity";
import CaregiverSurveyEntry from "./CaregiverSurveyEntry/CaregiverSurveyEntry";
import CaregiverSurveyFinish from "./CaregiverSurveyFinish/CaregiverSurveyFinish";
import CaregiverSurveyQuestion from "./CaregiverSurveyQuestion/CaregiverSurveyQuestion";

interface Props {
  surveyState: SurveyState;
  language: OrientationLanguages;
  currentQuestion: CaregiverSurveyQuestionWithPossibleAnswers | null;
  progressPerecentage: string;
  completionText: string | null;

  onClickStartCaregiverSurvey: () => void;
  onClickSurveyNotInterested: () => void;
  onClickFinishCaregiverSurvey: () => void;
  onSubmitAnswer: (
    questionId: CaregiverSurveyQuestionId,
    answerIds: CaregiverSurveyQuestionAnswerId[]
  ) => void;
}

const CaregiverSurveyScreen = (props: Props) => {
  const renderSurveyScreen = React.useCallback(() => {
    switch (props.surveyState) {
      case "Welcome": {
        return (
          <CaregiverSurveyEntry
            onClickNotInterested={props.onClickSurveyNotInterested}
            onClickStart={props.onClickStartCaregiverSurvey}
          />
        );
      }
      case "Questions": {
        {
          if (props.currentQuestion === null || isLoading(props.language)) {
            return null;
          }

          return (
            <CaregiverSurveyQuestion
              language={props.language}
              key={CaregiverSurveyQuestionId.unwrap(props.currentQuestion.id)}
              question={props.currentQuestion}
              questionsPercentage={props.progressPerecentage}
              questionsLeftText={props.completionText}
              onSubmitAnswer={props.onSubmitAnswer}
            />
          );
        }
      }
      case "Finish": {
        return <CaregiverSurveyFinish onClickFinish={props.onClickFinishCaregiverSurvey} />;
      }
      default: {
        assertNever(props.surveyState);
      }
    }
  }, [
    props.surveyState,
    props.currentQuestion,
    props.language,
    props.progressPerecentage,
    props.completionText,
    props.onClickStartCaregiverSurvey,
    props.onClickSurveyNotInterested,
    props.onClickFinishCaregiverSurvey,
    props.onSubmitAnswer,
  ]);

  return (
    <AppAbstractModal isOpen={true} className="CaregiverSurveyScreen">
      {renderSurveyScreen()}
    </AppAbstractModal>
  );
};

export default React.memo(CaregiverSurveyScreen);
